// © 2026 AttainIP Engineering
/**
 * Client–build skew check (RFC-0174 push spec M-12).
 *
 * A tab left open across a deploy keeps running the code it loaded, whatever
 * the server now serves — and nothing told it so. On 11 September a tab still
 * running removed code refreshed the whole results page every 30 s for hours.
 *
 * Each build bakes its id into the bundle (`import.meta.env.VITE_BUILD_ID`)
 * and writes the same id beside it as `build.json` (vite.config.ts). This hook
 * re-reads the served manifest only when there is a reason to — the tab became
 * visible again, or the network came back — never on a timer. A different id
 * means this tab is superseded; the caller offers a reload.
 *
 * Off in dev (Vite serves no manifest) and whenever the running id is unknown.
 */
import { useEffect, useRef, useState } from 'react';

export const RUNNING_BUILD_ID: string = import.meta.env.VITE_BUILD_ID ?? 'dev';

/** True only when both ids are real and differ. */
export function isSuperseded(runningId: string | null | undefined, servedId: string | null | undefined): boolean {
  if (!runningId || runningId === 'dev' || !servedId) return false;
  return servedId !== runningId;
}

export interface BuildSkewOptions {
  runningId?: string;
  manifestUrl?: string;
  enabled?: boolean;
}

export function useBuildSkewCheck(options: BuildSkewOptions = {}): boolean {
  const {
    runningId = RUNNING_BUILD_ID,
    manifestUrl = `${import.meta.env.BASE_URL}build.json`,
    enabled = !import.meta.env.DEV,
  } = options;
  const [superseded, setSuperseded] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!enabled || runningId === 'dev' || superseded) return;
    let disposed = false;

    async function check() {
      if (inFlight.current || disposed) return;
      inFlight.current = true;
      try {
        const res = await fetch(`${manifestUrl}?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const body = (await res.json()) as { build_id?: unknown };
        const served = typeof body.build_id === 'string' ? body.build_id : null;
        if (!disposed && isSuperseded(runningId, served)) setSuperseded(true);
      } catch {
        // No manifest or no network: says nothing about skew.
      } finally {
        inFlight.current = false;
      }
    }

    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    const onOnline = () => { void check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, runningId, manifestUrl, superseded]);

  return superseded;
}
